import createHistory from 'history/createHashHistory'; // https://github.com/reacttraining/history
import queryString from '../../utilities/queryString';
import GeckoStore from 'services/gecko-store';
import { setPage, setParams, setSlug } from 'actions/location';
import { useState, useCallback, useEffect } from 'react';

const history = createHistory();

const GeckoLocation = {
  get history() {
    return history;
  },
  get location() {
    return history.location;
  },
  get params() {
    const queryObject = queryString.parse(this.location.search);
    return queryObject.hasOwnProperty('search')
      ? {
          ...queryObject,
          search: String(queryObject.search),
        }
      : queryObject;
  },
  get urlParam() {
    return history.location.pathname.split('/').pop();
  },
  goTo(path, params, { force } = { force: false }) {
    const qs = params ? queryString.stringify(params) : '';
    // Fire all before change callbacks coecering them to promises and quit early if any of them return false or reject;
    const promises = this.beforeCallbacks.map((cb) => cb(path, params));
    return Promise.all(promises).then((results) => {
      if (!results.includes(false)) {
        // if none of the promises resolved false
        // Change the actual URL, changeCallbacks are used in the historys listen function defined below
        return force
          ? history.replace(`${path}${qs}`)
          : history.push(`${path}${qs}`);
      } else {
        throw new Error(); // reject the promise
      }
    });
  },
  reload() {
    return this.goTo(this.location.pathname, this.params, { force: true });
  },
  changeCallbacks: [],
  // Adds a callback to the list of callbacks that will be called when the route has changed
  onChange(callback) {
    this.changeCallbacks = [].concat(this.changeCallbacks, callback);
    return () => {
      // handle unlistening
      this.changeCallbacks = this.changeCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  },
  beforeCallbacks: [],
  // Adds a callback to the list of callbacks that will be called before the route changes, if any of these functions explicitly return false the route change will cancel
  beforeChange(callback) {
    this.beforeCallbacks = [].concat(this.beforeCallbacks, callback);
    return () => {
      // handle unlistening
      this.beforeCallbacks = this.beforeCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  },
};

history.listen((location, action) => {
  for (var index = 0; index < GeckoLocation.changeCallbacks.length; index++) {
    const callback = GeckoLocation.changeCallbacks[index];
    callback(location, action);
  }
  GeckoStore.redux.dispatch(setPage(GeckoLocation.location.pathname));
  GeckoStore.redux.dispatch(setParams(GeckoLocation.params));
  GeckoStore.redux.dispatch(
    setSlug(GeckoLocation.location.pathname.split('/').slice(-1)[0])
  );
});

GeckoStore.redux.dispatch(setPage(GeckoLocation.location.pathname));
GeckoStore.redux.dispatch(setParams(GeckoLocation.params));
GeckoStore.redux.dispatch(
  setSlug(GeckoLocation.location.pathname.split('/').slice(-1)[0])
);

export const useQueryStringParameter = (key) => {
  const decodeQuerystringValue = useCallback((value) => {
    if (!value) {
      return undefined;
    }
    return decodeURIComponent(value);
  }, []);

  const encodeQuerystringValue = useCallback((value) => {
    if (!value) {
      return undefined;
    }
    return encodeURIComponent(value);
  }, []);

  const [value, setValue] = useState(
    decodeQuerystringValue(GeckoLocation.params[key])
  );
  const setCurrentValue = useCallback(() => {
    return setValue(decodeQuerystringValue(GeckoLocation.params[key]));
  }, [key]);

  useEffect(() => {
    setCurrentValue();
  }, [setCurrentValue]);

  useEffect(() => {
    return GeckoLocation.onChange(() => {
      setCurrentValue();
    });
  }, [setCurrentValue]);

  const setNewValue = useCallback(
    (newValue) => {
      GeckoLocation.goTo(GeckoLocation.location.pathname, {
        ...GeckoLocation.params,
        [key]: encodeQuerystringValue(newValue),
      });
    },
    [key]
  );

  return [value, setNewValue];
};

export default GeckoLocation;
